<template>
  <b-col class="px-0 w-full col-md-12" md="12">
    <ValidationProvider
      name="Company Name"
      rules="required"
      v-slot="{ errors }"
    >
      <b-form-group
        label="Company *"
        for="Company Name"
        class="position-relative pb-3"
      >
        <multiselect
          v-model="selectedCompany"
          :options="companyNames"
          :allow-empty="false"
          :taggable="true"
          :disabled="success"
          class="custom-multiselect"
          placeholder="Select company"
          label="text"
          track-by="value"
          hideSelected
          tag-placeholder="Add this as a new Company"
          @input="onSelectCompany"
          @tag="onAddCompany"
          data-cy="customer-add-field-company"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            {{ ellipsify(option.text, 45) }}
          </template>
          <template slot="noResult">No companies found</template>
        </multiselect>
        <h6
          v-if="errors && errors[0]"
          class="mt-2 font-sm font-family-regular position-absolute danger"
          data-cy="customer-add-field-error-company"
        >
          {{ errors[0] }}
        </h6>
      </b-form-group>
    </ValidationProvider>

    <ValidationProvider
      v-if="isExistingCompany"
      name="Company Membership"
      rules="required"
      v-slot="{ errors }"
    >
      <b-form-group
        label="Membership *"
        for="Company Membership"
        class="position-relative pb-3"
      >
        <b-form-select
          v-model="selectedMembership.value"
          :key="selectedMembership.value"
          :options="membershipOptions"
          :disabled="success"
          @input="promptUpdateSubscription($event, selectedMembershipId)"
          data-cy="customer-add-field-membership"
        ></b-form-select>

        <h6
          v-if="errors && errors[0]"
          class="mt-2 font-sm font-family-regular position-absolute danger"
          data-cy="customer-add-field-error-membership"
        >
          {{ errors[0] }}
        </h6>
      </b-form-group>
    </ValidationProvider>

    <ValidationProvider
      v-else
      name="billing-entity"
      rules="required"
      v-slot="{ errors }"
    >
      <b-form-group
        for="billing-entity"
        label="Billing Entity *"
        class="position-relative pb-3"
      >
        <b-form-select
          v-model="selectedHeadroomEntity.value"
          :key="selectedHeadroomEntity.value"
          :options="headroomEntities"
          :disabled="success"
          @input="setCompanyBillingEntity"
          data-cy="customer-add-field-billing-entity"
        ></b-form-select>

        <h6
          v-if="errors && errors[0]"
          class="mt-2 font-sm font-family-regular position-absolute danger"
          data-cy="customer-add-field-error-billing-entity"
        >
          {{ errors[0] }}
        </h6>
      </b-form-group>
    </ValidationProvider>

    <ChangeSubscriptionModal
      ref="subModal"
      v-if="selectedCompany.id"
      :id="selectedCompany.id"
      :name="selectedCompany.name"
      :error="membershipError"
      :can-close="canCloseSubModal"
      @update-confirmed="confirmUpdateSubscription"
      @update-cancelled="cancelSubscriptionUpdate"
    />
  </b-col>
</template>

<script>
import {
  ValidationProvider,
  extend
} from "vee-validate/dist/vee-validate.full";

import Multiselect from "vue-multiselect";

import { ellipsify } from "@/utils/utils";

import ChangeSubscriptionModal from "@/views/admin/companies/ChangeSubscriptionModal.vue";

extend("required", {
  validate(item) {
    // * We extend the `required` rule to match the specific case of having a dropdown
    // * that has an object as its `v-model`.
    // * Its value will be an object, hence, not empty, yet we are interested in the object's
    // * `text` and `value` properties.

    // eslint-disable-next-line
    const hasText = typeof item === "object" && !!item && !!item.text;
    if (hasText) {
      const doesNotIncludeDefaultText = !item.text
        .toLowerCase()
        .includes("please select");

      return !!item.value || doesNotIncludeDefaultText;
    }

    return !!item;
  }
});

export default {
  components: {
    ValidationProvider,
    Multiselect,
    ChangeSubscriptionModal
  },

  props: {
    success: { type: Boolean },
    memberships: { type: Array, required: true },
    currentMembershipOption: { type: Object, default: null },
    companyNames: { type: Array, required: true },
    headroomEntities: { type: Array, required: true },
    selectedMembershipId: { type: String },
    membershipError: { type: String },
    canCloseSubModal: { type: Boolean }
  },

  data() {
    return {
      isOrWasNewCompanyProcessed: false,

      selectedCompany: {
        value: null,
        text: "Please select a company"
      },

      selectedMembership: {
        value: null,
        text: "Please select a membership"
      },

      selectedHeadroomEntity: {
        value: null,
        text: "Please select a billing entity"
      },

      prevMembershipId: "",
      prevMembershipLabel: "",

      requiredFieldErrorMessage: "This field is required."
    };
  },

  computed: {
    isExistingCompany() {
      // * We want to execute the code for existing company only if an actual one is made.
      // * If the user selects back the "Select a company" option, we want to reset view to the initial state
      // * Since `this.selectedMembershipId` is a `prop` it is best not to update it's value here
      // * However, `this.selectedCompany.value` is set to `null`
      return this.selectedMembershipId && this.selectedCompany?.value;
    },

    membershipOptions() {
      const options = this.memberships || [];
      const current = this.currentMembershipOption;
      if (!current?.value) {
        return options;
      }

      const alreadyListed = options.some(({ value }) => value === current.value);
      if (alreadyListed) {
        return options;
      }

      return [...options, current];
    }
  },

  methods: {
    ellipsify,

    onSelectCompany({ value: companyId }) {
      const noValue = !companyId;
      if (noValue) {
        this.selectedCompany.value = null;
        return;
      }

      this.selectedCompany.id = companyId;
      this.$emit("selected-company-id", companyId);
    },

    onAddCompany(companyName) {
      this.selectedCompany = { value: null, text: companyName };
      this.$emit("new-company-name", companyName);
    },

    // todo: can be a mixin for `ChangeSubscriptionModal`
    async promptUpdateSubscription(updateMembershipId) {
      const updateMembershipLabel = this.getUpdateMembershipLabel(
        updateMembershipId
      );
      const allowedFlexibleHours = this.checkIfIsFlexibleHours(
        updateMembershipId
      );

      await this.$refs.subModal.open(
        this.prevMembershipId,
        this.prevMembershipLabel,
        updateMembershipLabel,
        updateMembershipId,
        allowedFlexibleHours
      );
    },

    setCompanyBillingEntity(entity) {
      this.selectedHeadroomEntity = { value: entity };
      this.$emit("new-company-entity", entity);
    },

    // todo: can be a mixin for `ChangeSubscriptionModal`
    confirmUpdateSubscription(companyId, id, label, hoursNeeded = null) {
      // * We keep the latest value that was set before the current change
      // * Current change here refers to the `promptUpdateSubscription` method
      // * being called with the latest `title` value.
      this.prevMembershipId = id;
      this.prevMembershipLabel = label;

      const data = { data: { id: companyId, new_membership: id } };
      if (hoursNeeded) {
        data.data.total_hours = hoursNeeded;
      }

      this.$emit("confirm-update-membership", data);
    },

    // todo: can be a mixin for `ChangeSubscriptionModal`
    cancelSubscriptionUpdate() {
      this.membershipId = this.selectedMembershipId;
      this.prevMembershipLabel = this.selectedMembership.text;
      this.selectedMembership = { value: this.membershipId };
      this.$emit("cancel-update-membership");
    },

    // todo: can be a mixin for `ChangeSubscriptionModal`
    checkIfIsFlexibleHours(membershipId) {
      return this.membershipOptions.find(({ value }) => value === membershipId)
        ?.allowedFlexibleHours;
    },

    // todo: can be a mixin for `ChangeSubscriptionModal`
    getUpdateMembershipLabel(membershipId) {
      return this.membershipOptions.find(({ value }) => value === membershipId)
        ?.text;
    }
  },

  watch: {
    selectedMembershipId: {
      immediate: true,
      handler(newId) {
        if (!newId) {
          this.selectedMembership = { value: null };
          return;
        }

        this.prevMembershipId = this.selectedMembershipId;
        this.prevMembershipLabel =
          this.selectedMembershipId &&
          this.getUpdateMembershipLabel(this.selectedMembershipId);

        // * Needs to be an object to trigger the change
        this.selectedMembership = { value: newId };
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
